import { GoverningJurisdictionTypeId } from '../../../../../models/enum/shared/governing-jurisdiction-type-id';
import { AllocationMode } from '../../../../accounts/shared/types/allocation-mode-type';
import { effectiveJurisdictionId, floorToCents } from '../utils/asset-selection-rules';
import { UnlockFundsModalConfig, UnlockFundsModalRow } from './unlock-funds-modal-config';

export const MAX_UNLOCK_PERCENTAGE = 50;

export interface UnlockFundsModalSummary {
  finalLocationId: GoverningJurisdictionTypeId | null;
  rulesJurisdictionId: GoverningJurisdictionTypeId | null;
  lockedBalance: number;
  maxDollarAmount: number;
  isMaxJurisdiction: boolean;
  isPooled: boolean;
  hasExistingUnlock: boolean;
}

export interface UnlockFundsEntry {
  mode: AllocationMode;
  percentage: number | null;
  amount: number | null;
}

export function summarizeUnlockFundsConfig(config: UnlockFundsModalConfig): UnlockFundsModalSummary {
  const rulesJurisdictionId = unlockRulesJurisdictionId(config);
  const lockedBalance = config.rows.reduce((sum, row) => sum + row.lockedBalance, 0);
  return {
    finalLocationId: config.finalLocationId,
    rulesJurisdictionId,
    lockedBalance,
    maxDollarAmount: floorToCents(lockedBalance * (MAX_UNLOCK_PERCENTAGE / 100)),
    isMaxJurisdiction: rulesJurisdictionId !== null && config.maxJurisdictions.includes(rulesJurisdictionId),
    isPooled: config.mode === 'pooled',
    hasExistingUnlock: config.rows.some(({ formGroup }) => hasUnlock(formGroup.controls))
  };
}

/** The final location in use, or under Default(s) the covered rows' own jurisdiction. */
export function unlockRulesJurisdictionId(config: UnlockFundsModalConfig): GoverningJurisdictionTypeId | null {
  const ownJurisdictionIds = new Set(config.rows.map(row => row.formGroup.controls.governingJurisdictionId.value));
  const [ownJurisdictionId] = ownJurisdictionIds.size === 1 ? Array.from(ownJurisdictionIds) : [null];
  return effectiveJurisdictionId(ownJurisdictionId, config.finalLocationId);
}

/** What the modal reopens with on Edit Unlock, in the mode the unlock was saved in. */
export function savedUnlockEntry(config: UnlockFundsModalConfig): UnlockFundsEntry {
  return config.mode === 'pooled' ? savedPooledEntry(config.rows) : savedSingleEntry(config.rows);
}

/** One percentage applies cleanly to each source jurisdiction's pot, so every covered row gets the same one. */
export function applyPooledUnlock(rows: UnlockFundsModalRow[], percentage: number): void {
  rows.forEach(({ formGroup }) => {
    formGroup.controls.lockedAmountToUnlock.setValue(null);
    formGroup.controls.lockedPercentageToUnlock.setValue(roundToHundredths(percentage));
  });
}

/** Stores whichever of percentage or dollars the user entered; the backend accepts one or the other, never both. */
export function applySingleUnlock(rows: UnlockFundsModalRow[], entry: UnlockFundsEntry): void {
  const isPercent = entry.mode === 'percent';
  const percentage = isPercent && entry.percentage !== null ? roundToHundredths(entry.percentage) : null;
  const amount = isPercent ? null : entry.amount;
  rows.forEach(({ formGroup }) => {
    formGroup.controls.lockedAmountToUnlock.setValue(amount);
    formGroup.controls.lockedPercentageToUnlock.setValue(percentage);
  });
}

export function clearUnlock(rows: UnlockFundsModalRow[]): void {
  rows.forEach(({ formGroup }) => {
    formGroup.controls.lockedAmountToUnlock.setValue(null);
    formGroup.controls.lockedPercentageToUnlock.setValue(null);
  });
}

export function ceilToHundredths(value: number): number {
  return Math.ceil(value * 100) / 100;
}

/** The percentage column holds two decimal places. */
function roundToHundredths(value: number): number {
  return Math.round(value * 100) / 100;
}

function hasUnlock(controls: UnlockFundsModalRow['formGroup']['controls']): boolean {
  return controls.lockedAmountToUnlock.value !== null || controls.lockedPercentageToUnlock.value !== null;
}

function savedPooledEntry(rows: UnlockFundsModalRow[]): UnlockFundsEntry {
  const savedPercentage = rows
    .map(({ formGroup }) => formGroup.controls.lockedPercentageToUnlock.value)
    .find(percentage => percentage !== null);
  return { mode: 'percent', percentage: savedPercentage ?? null, amount: null };
}

function savedSingleEntry(rows: UnlockFundsModalRow[]): UnlockFundsEntry {
  const [row] = rows;
  const amount = row?.formGroup.controls.lockedAmountToUnlock.value ?? null;
  const percentage = row?.formGroup.controls.lockedPercentageToUnlock.value ?? null;
  const lockedBalance = row?.lockedBalance ?? 0;

  if (amount !== null) {
    const derivedPercentage = lockedBalance > 0 ? ceilToHundredths((amount / lockedBalance) * 100) : null;
    return { mode: 'dollar', percentage: derivedPercentage, amount };
  }
  if (percentage !== null) {
    return { mode: 'percent', percentage, amount: floorToCents(lockedBalance * (percentage / 100)) };
  }
  return { mode: 'percent', percentage: null, amount: null };
}
